import { Injectable } from '@angular/core';
import { ApiService, AppSettingsService, LoggingService, StatefulService } from '@woolworthsnz/styleguide';
import {
	Address,
	AddressEligibilityResponse,
	BillingAddressRequest,
	CreateSubscriptionRequest,
	DeliverySubscriptionCreateSubscriptionResponse,
	DeliverySubscriptionDetail,
	DeliverySubscriptionDetailsResponse,
	DeliverySubscriptionPlan,
	DeliverySubscriptionPlansResponse,
	ShopperAddressWithSubscriptionEligibility,
} from '@woolworthsnz/trader-api';
import { Observable, of } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';

export interface DeliverySubscriptionState {
	selectedPlan?: DeliverySubscriptionPlan;
	isShopperEligibleForFreeTrial?: boolean;
	currentSubscription?: DeliverySubscriptionDetail;
	shopperAddressWithSubscriptionEligibility?: ShopperAddressWithSubscriptionEligibility[];
	isPrimaryAddressEligibleForSubscription: boolean;
	allPlans: DeliverySubscriptionPlan[];
	basePlan?: DeliverySubscriptionPlan;
}

const initialState: DeliverySubscriptionState = {
	selectedPlan: undefined,
	isShopperEligibleForFreeTrial: true,
	currentSubscription: undefined,
	shopperAddressWithSubscriptionEligibility: [],
	isPrimaryAddressEligibleForSubscription: false,
	allPlans: [],
};

@Injectable({
	providedIn: 'root',
})
export class DeliverySubscriptionService extends StatefulService<DeliverySubscriptionState> {
	constructor(
		private appSettingsService: AppSettingsService,
		private apiService: ApiService,
		private loggingService: LoggingService
	) {
		super(initialState);
	}

	get plansEndpoint(): string {
		return this.appSettingsService.getEndpoint('deliverySubscriptionGetPlans');
	}

	get createSubscriptionEndpoint(): string {
		return this.appSettingsService.getEndpoint('deliverySubscriptionCreateSubscription');
	}

	get myDeliverySubscriptionEndpoint(): string {
		return this.appSettingsService.getEndpoint('deliverySubscriptionMySubscription');
	}

	get addressEligibilityEndpoint(): string {
		return this.appSettingsService.getEndpoint('deliverySubscriptionAddressEligibility');
	}

	get paymentInstrumentsEndpoint(): string {
		return this.appSettingsService.getEndpoint('paymentInstruments');
	}

	getPlans(): void {
		this.apiService
			.get(this.plansEndpoint, {})
			.pipe(
				take(1),
				catchError((error) => {
					this.loggingService.trackEvent(`UI:DeliverySubscription`, {
						error,
					});
					return of(error);
				})
			)
			.subscribe((r: DeliverySubscriptionPlansResponse) => {
				if (r.plans != null) {
					const allPlans = this.sortPlans(r.plans);
					this.setState({
						allPlans: allPlans,
						selectedPlan: allPlans[0],
						basePlan: this.getBasePlan(r.plans),
					});
				}
			});
	}

	sortPlans(plans: Array<DeliverySubscriptionPlan>): DeliverySubscriptionPlan[] {
		return plans.sort((a, b) => {
			if (a.displayOrder === undefined || b.displayOrder === undefined) {
				return -1;
			} else {
				return a.displayOrder - b.displayOrder;
			}
		});
	}

	getBasePlan(plans: Array<DeliverySubscriptionPlan>): DeliverySubscriptionPlan {
		plans.sort((a, b) => {
			if (a.price === undefined || b.price === undefined) {
				return -1;
			} else {
				return a.price - b.price;
			}
		});
		return plans[0];
	}

	getMySubscription(): Observable<DeliverySubscriptionDetailsResponse> {
		return this.apiService.get(this.myDeliverySubscriptionEndpoint).pipe(
			tap((deliverySubscriptionDetailsResponse: DeliverySubscriptionDetailsResponse) => {
				this.setState({
					isShopperEligibleForFreeTrial:
						deliverySubscriptionDetailsResponse?.isShopperEligibleForFreeTrial ?? true,
				});
			}),
			catchError((error) => {
				this.loggingService.trackEvent(`UI:DeliverySubscription`, {
					error,
				});
				return of(error);
			})
		);
	}

	setCurrentSubscription = (currentSubscription: DeliverySubscriptionDetail): void => {
		this.setState({
			currentSubscription: currentSubscription,
		});
	};

	createSubscription(
		createSubscriptionRequest: CreateSubscriptionRequest
	): Observable<DeliverySubscriptionCreateSubscriptionResponse> {
		return this.apiService.post(this.createSubscriptionEndpoint, createSubscriptionRequest).pipe(
			catchError((error) => {
				this.loggingService.trackEvent(`UI:DeliverySubscription`, {
					error,
				});
				return of(error);
			})
		);
	}

	setSelectedPlan = (selectedPlan: DeliverySubscriptionPlan): void => {
		this.setState({
			selectedPlan: selectedPlan,
		});
	};

	getSelectedPlan(): DeliverySubscriptionPlan | undefined {
		return this.state.selectedPlan;
	}

	getCurrentSubscription(): DeliverySubscriptionDetail | undefined {
		return this.state.currentSubscription;
	}

	getBillingAddressFromRawValues(addressRawValues: any): BillingAddressRequest {
		const isManual = addressRawValues.addressProviderId == null;

		return isManual
			? this.getBillingAddressFromManualAddressEntry(addressRawValues)
			: this.getBillingAddressFromProviderAddress(addressRawValues);
	}

	getBillingAddressFromManualAddressEntry(addressRawValues: any): BillingAddressRequest {
		return {
			isNewAddress: true,
			isManual: true,
			street1:
				(addressRawValues.manualUnit ? addressRawValues.manualUnit + ', ' : '') +
				addressRawValues.manualStreetAddress,
			suburb: addressRawValues.manualSuburb,
			city: addressRawValues.manualTownCity,
			country: 'New Zealand',
			postcode: addressRawValues.manualPostcode,
		};
	}

	getBillingAddressFromProviderAddress(addressRawValues: any): BillingAddressRequest {
		return {
			isNewAddress: true,
			isManual: false,
			addressProviderId: addressRawValues.addressProviderId,
			street1: (addressRawValues.unit ? addressRawValues.unit + ', ' : '') + addressRawValues.addressStreet1,
			street2: addressRawValues.addressStreet2,
			suburbId: +addressRawValues.suburbId,
			country: 'New Zealand',
		};
	}

	getBillingAddressFromExistingAddress(address?: Address): BillingAddressRequest {
		return {
			isNewAddress: false,
			isManual: false,
			addressProviderId: address?.addressProviderId,
			street1: address?.street1,
			suburbId: address?.suburbId,
			suburb: address?.suburb,
			street2: address?.street2 !== address?.suburb ? address?.street2 : '',
			city: address?.city,
			country: address?.country,
			postcode: address?.postcode,
		};
	}

	getAddressEligibility(): Observable<any> {
		return this.apiService.get(this.addressEligibilityEndpoint).pipe(
			tap((res: AddressEligibilityResponse) => {
				this.setState({
					shopperAddressWithSubscriptionEligibility: res.shopperAddressesWithSubscriptionEligibility,
					isPrimaryAddressEligibleForSubscription: res.isPrimaryAddressEligibleForSubscription,
				});
			}),
			catchError((error) => {
				this.loggingService.trackEvent(`UI:DeliverySubscription`, {
					error,
				});
				return of(error);
			})
		);
	}
}
